const moduleLoadPerf = performance.now()
const STORAGE_KEY = 'beacon_logs'
const MAX_ENTRIES = 1500

interface LogEntry {
  wall: number
  rel: number
  tag: string
  msg: string
  data?: Record<string, unknown>
  session?: true
}

const buffer: LogEntry[] = (() => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? (JSON.parse(stored) as LogEntry[]) : []
  } catch {
    return []
  }
})()

// Mark the start of this session in the persistent buffer
const version = import.meta.env.VITE_BUNDLE_VERSION as string | undefined
buffer.push({ wall: Date.now(), rel: 0, tag: 'session', msg: version ? `app open · v${version}` : 'app open', session: true })

// Persisting serializes the whole buffer (~200 KB at the cap) and writes it
// synchronously, and Firestore logs every snapshot — doing that per entry cost
// 300–500 ms of main thread per page load. Entries batch for up to
// FLUSH_DELAY_MS, and anything pending is written as soon as the app is hidden,
// the last moment a WebView reliably runs code before it's frozen or killed.
const FLUSH_DELAY_MS = 1000
let flushTimer: ReturnType<typeof setTimeout> | null = null

function flush() {
  if (flushTimer !== null) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer))
  } catch {
    // localStorage full or blocked — keep in-memory buffer, skip persist
  }
}

function scheduleFlush() {
  flushTimer ??= setTimeout(flush, FLUSH_DELAY_MS)
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && flushTimer !== null) flush()
  })
  addEventListener('pagehide', () => {
    if (flushTimer !== null) flush()
  })
}

function record(tag: string, msg: string, data?: Record<string, unknown>) {
  const entry: LogEntry = {
    wall: Date.now(),
    rel: Math.round(performance.now() - moduleLoadPerf),
    tag,
    msg,
    ...(data !== undefined ? { data } : {}),
  }
  if (buffer.length >= MAX_ENTRIES) buffer.shift()
  buffer.push(entry)
  scheduleFlush()
}

// Writes to buffer + console.
export function log(tag: string, msg: string, data?: Record<string, unknown>) {
  record(tag, msg, data)
  if (data !== undefined) {
    console.log(`[${tag}] ${msg}`, data)
  } else {
    console.log(`[${tag}] ${msg}`)
  }
}

// Writes to buffer only — use when another logger already handles console output.
export function logFile(tag: string, msg: string, data?: Record<string, unknown>) {
  record(tag, msg, data)
}

function formatRel(rel: number): string {
  const mm = Math.floor(rel / 60000).toString().padStart(2, '0')
  const ss = Math.floor((rel % 60000) / 1000).toString().padStart(2, '0')
  const ms = (rel % 1000).toString().padStart(3, '0')
  return `${mm}:${ss}.${ms}`
}

export function exportLogs(): string {
  if (buffer.length === 0) return ''
  const lines = buffer.map(e => {
    if (e.session) {
      const ts = new Date(e.wall).toISOString()
      return `\n──── ${e.msg} · ${ts} ────`
    }
    const ts = new Date(e.wall).toISOString()
    const data = e.data ? ` ${JSON.stringify(e.data)}` : ''
    return `${ts} ${formatRel(e.rel)}: [${e.tag}] ${e.msg}${data}`
  })
  const header = `Faro log - ${new Date().toISOString()} - ${buffer.length} entries\n${'─'.repeat(60)}`
  return header + '\n' + lines.join('\n')
}

export function clearLogs() {
  buffer.length = 0
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
